/**
 * useNoticeSummary — shared notice-to-vacate counts for the Notices nav badge
 * (A1, mirroring `useMakeReadySummary` / `useAlertSummary`).
 *
 * Polls the cheap counts-only `GET /notices/summary` every 60s + on window
 * focus. The Notices nav badge consumes this ONE query (same key → TanStack
 * Query dedupes to a single request). `pendingConfirmation` is the badge value:
 * AI-classified notices sitting in PENDING_CONFIRMATION that a PM must confirm
 * to start the unit turn.
 *
 * Enabled only for roles that see the Notices nav item (ORG_ADMIN / PM_MANAGER /
 * MAINTENANCE) or for ops users actively viewing an org. Disabled otherwise so
 * we never poll blind for VIEWER / logged-out / bare-ops sessions. The summary
 * endpoint is itself property-scoped + fail-closed on the server.
 */
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const NOTICE_ROLES = ['ORG_ADMIN', 'PM_MANAGER', 'MAINTENANCE'];

export interface NoticeSummaryCounts {
  pendingConfirmation: number;
}

const ZERO: NoticeSummaryCounts = { pendingConfirmation: 0 };

export function useNoticeSummary() {
  const { user, isOpsUser, opsOrgAccess } = useAuth();
  const role = user?.type === 'org' ? user.role : undefined;
  const enabled =
    (!isOpsUser && !!role && NOTICE_ROLES.includes(role)) ||
    (isOpsUser && !!opsOrgAccess);

  const query = useQuery({
    queryKey: ['notice-summary'],
    queryFn: async (): Promise<NoticeSummaryCounts> => {
      const res = await api.get('/notices/summary');
      const d = res.data?.data ?? res.data ?? {};
      return {
        pendingConfirmation: d.pendingConfirmation ?? d.PENDING_CONFIRMATION ?? 0,
      };
    },
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  return { counts: query.data ?? ZERO, isLoading: query.isLoading, enabled };
}
